import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, UncontrolledTooltip } from "reactstrap";
import axios from "axios";
import { getCookie } from "../jwt/_helpers/cookie";
import { isDemoMode, isBrowserSessionDemo } from "../utils/demoConfig";
import {
  WL_DEMO_TRACK_AUDIO_PLACEHOLDER,
  WL_DEMO_TRACK_AUDIO_PLACEHOLDER_MIME,
} from "../utils/demoTrackAudioPlaceholder";
import { getDemoTrackBlob, isWlDemoBlobRef, wlDemoBlobStorageKeyFromRef } from "../utils/demoTrackBlobStore";

const PROGRESS_INCREMENT = 0.05;
const MIN_COMPLETION_RATIO = 0.95;
const MIN_UNIQUE_SECONDS_PER_CHUNK = 5;

const mergeIntervals = (intervals, candidate) => {
  if (!candidate || candidate[1] <= candidate[0]) {
    return intervals;
  }

  const merged = [];
  const sorted = [...intervals, candidate].sort((a, b) => a[0] - b[0]);

  sorted.forEach(([start, end]) => {
    if (!merged.length || start > merged[merged.length - 1][1]) {
      merged.push([start, end]);
    } else {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
    }
  });

  return merged;
};

const getIntervalTotal = (intervals) =>
  intervals.reduce((total, [start, end]) => total + Math.max(0, end - start), 0);

const sanitizeRange = (start, end, duration) => {
  const safeStart = Math.max(0, Math.min(start, duration ?? start));
  const safeEnd = Math.max(safeStart, Math.min(end, duration ?? end));
  return [safeStart, safeEnd];
};
const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const parseDurationToSeconds = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== "string") {
    return 0;
  }
  const chunks = value.split(":").map((part) => Number(part));
  if (chunks.some((part) => Number.isNaN(part))) {
    return 0;
  }
  if (chunks.length === 3) {
    return chunks[0] * 3600 + chunks[1] * 60 + chunks[2];
  }
  if (chunks.length === 2) {
    return chunks[0] * 60 + chunks[1];
  }
  if (chunks.length === 1) {
    return chunks[0];
  }
  return 0;
};

const getTrackListenId = (track, index = 0) => {
  if (track?.id != null) {
    return track.id;
  }
  if (track?.order != null) {
    return `order-${track.order}`;
  }
  return `idx-${index}`;
};

const normalizeTracksForListening = (tracks) => {
  if (!Array.isArray(tracks)) {
    return [];
  }
  return tracks
    .map((track, index) => ({
      ...track,
      listenId: getTrackListenId(track, index),
      listened_duration: parseDurationToSeconds(track?.listened_duration),
    }))
    .filter((track) => track && (track.name || track.resource || track.mp3_resource));
};

const ListenTracks = ({
  id,
  track_urls,
  setForceUpdate,
  pre_scan,
  pre_scan_done,
  preScanEnabled,
  atleast_33_pct_listened_per_track,
  compact = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [progressByTrack, setProgressByTrack] = useState({});
  const [loadingTracks, setLoadingTracks] = useState({});
  const [failedTracks, setFailedTracks] = useState({});
  const [demoListenBlobUrls, setDemoListenBlobUrls] = useState({});
  const [demoListenBlobMimes, setDemoListenBlobMimes] = useState({});
  const [fetchedTracks, setFetchedTracks] = useState([]);
  const [loadingFetchedTracks, setLoadingFetchedTracks] = useState(false);
  const progressCacheRef = useRef({});
  const listenedSecondsRef = useRef({});
  const listenedIntervalsRef = useRef({});
  const lastTimeRef = useRef({});
  const trackDurationsRef = useRef({});
  const audioRefs = useRef({});
  const tracksListRef = useRef(null);
  const pendingRefreshRef = useRef(false);
  
  const preScanDeclarationRequired = pre_scan_done && pre_scan?.alert && pre_scan?.scanned_tracks;
  const preScanDeclarationSubmitted = pre_scan && pre_scan.admin_declaration;
  const activeTracks = useMemo(() => {
    const rowTracks = normalizeTracksForListening(track_urls);
    if (rowTracks.length > 0) {
      return rowTracks;
    }
    return normalizeTracksForListening(fetchedTracks);
  }, [track_urls, fetchedTracks]);

  const toggleModal = () => {
    const wasOpen = isModalOpen;
    setIsModalOpen(!isModalOpen);

    if (
      wasOpen && pendingRefreshRef.current
    ) {
      setTimeout(() => {
        setForceUpdate(prev => prev + 1);
        pendingRefreshRef.current = false;
      }, 150);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      return undefined;
    }
    const root = tracksListRef.current;
    if (root) {
      root.querySelectorAll("audio").forEach((audio) => {
        if (!audio.paused) {
          audio.pause();
        }
      });
    }
    return undefined;
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }

    const root = tracksListRef.current;
    if (!root) {
      return undefined;
    }

    const onPlayCapture = (event) => {
      const playing = event.target;
      if (!(playing instanceof HTMLAudioElement) || !root.contains(playing)) {
        return;
      }

      root.querySelectorAll("audio").forEach((other) => {
        if (other !== playing && !other.paused) {
          other.pause();
        }
      });

      const listenId = playing.id.startsWith("track_play_")
        ? playing.id.slice("track_play_".length)
        : "";
      if (listenId) {
        lastTimeRef.current[listenId] = playing.currentTime || 0;
      }
    };

    root.addEventListener("play", onPlayCapture, true);
    return () => {
      root.removeEventListener("play", onPlayCapture, true);
    };
  }, [isModalOpen, activeTracks, demoListenBlobUrls, loadingFetchedTracks, failedTracks]);

  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }

    const rowTracks = normalizeTracksForListening(track_urls);
    if (rowTracks.length > 0) {
      setLoadingFetchedTracks(false);
      return undefined;
    }

    let cancelled = false;
    const tokenAPI = getCookie("token");
    setLoadingFetchedTracks(true);

    axios
      .get(`${API_URL}releases/${id}/`, {
        headers: {
          Authorization: tokenAPI ? `Bearer ${tokenAPI}` : "",
          "x-api-key": X_API_KEY,
          "Content-Type": "application/json"
        }
      })
      .then((res) => {
        if (cancelled) {
          return;
        }
        const releaseTracks = Array.isArray(res?.data?.track_urls) && res.data.track_urls.length > 0
          ? res.data.track_urls
          : (Array.isArray(res?.data?.tracks) ? res.data.tracks : []);
        setFetchedTracks(normalizeTracksForListening(releaseTracks));
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("[ListenTracks] Failed to load release tracks:", error);
          setFetchedTracks([]);
        }
      })
      .finally(() => {
        setLoadingFetchedTracks(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isModalOpen, id, track_urls]);

  useEffect(() => {
    setFetchedTracks([]);
  }, [id]);

  const recordUniqueListening = (trackId, start, end, duration) => {
    if (!trackId || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      return listenedSecondsRef.current[trackId] || 0;
    }

    const [safeStart, safeEnd] = sanitizeRange(start, end, duration);
    const currentIntervals = listenedIntervalsRef.current[trackId] || [];
    const previousTotal = getIntervalTotal(currentIntervals);
    const durationBound = duration ?? safeEnd;

    const updatedIntervals = mergeIntervals(currentIntervals, [safeStart, safeEnd]);
    const nextTotal = Math.min(getIntervalTotal(updatedIntervals), durationBound);

    listenedIntervalsRef.current[trackId] = updatedIntervals;
    listenedSecondsRef.current[trackId] = nextTotal;

    return { previousTotal, nextTotal };
  };

  const hydrateProgressFromBackend = (trackId, listenedSeconds, duration) => {
    if (!trackId || !Number.isFinite(listenedSeconds) || listenedSeconds <= 0) {
      return;
    }

    const safeDuration = duration ?? trackDurationsRef.current[trackId];
    if (!safeDuration || safeDuration <= 0) {
      return;
    }

    const safeSeconds = Math.min(listenedSeconds, safeDuration);
    const existingSeconds = listenedSecondsRef.current[trackId] || 0;
    if (safeSeconds <= existingSeconds) {
      return;
    }

    listenedIntervalsRef.current[trackId] = [[0, safeSeconds]];
    listenedSecondsRef.current[trackId] = safeSeconds;
  };

  const describeMediaError = (error) => {
    if (!error) {
      return "Unknown audio error.";
    }
    switch (error.code) {
      case error.MEDIA_ERR_ABORTED:
        return "Playback aborted. Please try again.";
      case error.MEDIA_ERR_NETWORK:
        return "Network error while downloading the audio.";
      case error.MEDIA_ERR_DECODE:
        return "Audio decode error. The file may be corrupted or in an unsupported format.";
      case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
        return "Audio source format is not supported.";
      default:
        return "Unexpected audio error.";
    }
  };

  const handleAudioError = (track, event) => {
    if (event && typeof event.persist === "function") {
      event.persist();
    }

    const listenId = track.listenId;
    const mediaElement = event?.target;
    const nativeError = mediaElement?.error;
    const description = describeMediaError(nativeError);

    const fallbackSource = getAudioSource(track);
    const fallbackMime = getMimeType(track);

    console.error("[ListenTracks] Audio error", {
      trackId: track?.id,
      listenId,
      trackName: track?.name,
      source: fallbackSource,
      mimeType: fallbackMime,
      readyState: mediaElement?.readyState,
      networkState: mediaElement?.networkState,
      errorCode: nativeError?.code
    });


    setFailedTracks(prev => ({
      ...prev,
      [listenId]: true
    }));
    setLoadingTracks(prev => ({
      ...prev,
      [listenId]: false
    }));


    if (nativeError?.code !== nativeError?.MEDIA_ERR_NETWORK) {
      console.log(`Unable to play "${track.name}". ${description}`);
    }
  };

  const registerListenTrackAudio = (listenId, el) => {
    const prev = audioRefs.current[listenId];
    if (prev && prev !== el) {
      delete audioRefs.current[listenId];
    }

    if (el) {
      audioRefs.current[listenId] = el;
      return;
    }

    delete audioRefs.current[listenId];
  };

  const getAudioSource = (track) => {
    if (track.mp3_resource && typeof track.mp3_resource === "string") {
      const trimmed = track.mp3_resource.trim();
      return trimmed || null;
    }

    if (track.resource && typeof track.resource === "string") {
      const trimmed = track.resource.trim();
      return trimmed || null;
    }
    
    return null;
  };

  const getMimeType = (track) => {
    if (track.mp3_resource && String(track.mp3_resource).trim()) {
      return "audio/mpeg";
    }
    if (track.resource && String(track.resource).trim()) {
      return "audio/wav";
    }
    return undefined;
  };

  const resolveTrackAudio = (track) => {
    const listenId = track.listenId;
    const rawSource = getAudioSource(track);
    const isDemoBlobRef = Boolean(rawSource && isWlDemoBlobRef(String(rawSource).trim()));

    if (isDemoBlobRef) {
      const resolvedForBlob = demoListenBlobUrls[listenId];
      if (resolvedForBlob === undefined) {
        return {
          listenId,
          rawSource,
          audioSource: undefined,
          trackType: demoListenBlobMimes[listenId] || getMimeType(track),
          pendingDemoBlob: true,
        };
      }
      return {
        listenId,
        rawSource,
        audioSource: resolvedForBlob,
        trackType: demoListenBlobMimes[listenId] || getMimeType(track) || "audio/mpeg",
        pendingDemoBlob: false,
      };
    }

    if (rawSource) {
      return {
        listenId,
        rawSource,
        audioSource: rawSource,
        trackType: getMimeType(track),
        pendingDemoBlob: false,
      };
    }

    if (isDemoMode() || isBrowserSessionDemo()) {
      return {
        listenId,
        rawSource: null,
        audioSource: WL_DEMO_TRACK_AUDIO_PLACEHOLDER,
        trackType: WL_DEMO_TRACK_AUDIO_PLACEHOLDER_MIME,
        pendingDemoBlob: false,
      };
    }

    return {
      listenId,
      rawSource: null,
      audioSource: null,
      trackType: undefined,
      pendingDemoBlob: false,
    };
  };

  useEffect(() => {
    if (!isModalOpen || !activeTracks || activeTracks.length === 0) {
      return undefined;
    }
    let cancelled = false;
    const createdUrls = [];

    (async () => {
      const nextUrls = {};
      const nextMimes = {};
      for (const t of activeTracks) {
        const listenId = t.listenId;
        const raw = getAudioSource(t);
        if (!raw || !isWlDemoBlobRef(raw)) {
          continue;
        }
        const sk = wlDemoBlobStorageKeyFromRef(raw);
        try {
          const blob = await getDemoTrackBlob(sk);
          if (cancelled) {
            continue;
          }
          if (!blob) {
            nextUrls[listenId] = WL_DEMO_TRACK_AUDIO_PLACEHOLDER;
            nextMimes[listenId] = WL_DEMO_TRACK_AUDIO_PLACEHOLDER_MIME;
            continue;
          }
          const u = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(u);
            continue;
          }
          createdUrls.push(u);
          nextUrls[listenId] = u;
          nextMimes[listenId] = blob.type || "audio/wav";
        } catch (e) {
          console.warn("[ListenTracks] demo blob", e);
          if (!cancelled) {
            nextUrls[listenId] = WL_DEMO_TRACK_AUDIO_PLACEHOLDER;
            nextMimes[listenId] = WL_DEMO_TRACK_AUDIO_PLACEHOLDER_MIME;
          }
        }
      }
      if (!cancelled) {
        setDemoListenBlobUrls(nextUrls);
        setDemoListenBlobMimes(nextMimes);
      }
    })();

    return () => {
      cancelled = true;
      createdUrls.forEach(u => URL.revokeObjectURL(u));
      setDemoListenBlobUrls({});
      setDemoListenBlobMimes({});
    };
  }, [isModalOpen, activeTracks]);

  const secondsToHHMMSS = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };


  const updateTrackProgressState = (trackId, percent) => {
    setProgressByTrack(prev => {
      const current = prev[trackId] ?? 0;
      if (current >= 100) {
        return prev;
      }
      if (percent <= current) {
        return prev;
      }
      const nextValue = Math.min(100, Math.round(percent));
      return {
        ...prev,
        [trackId]: nextValue
      };
    });
  };

  const reportListeningProgress = async ({
    trackId,
    percentComplete,
    listenedSeconds
  }) => {
    const currentProgress = progressByTrack[trackId] ?? 0;
    if (currentProgress >= 100) {
      return;
    }
    
    const nextPercent =
      percentComplete >= 1
        ? 100
        : Math.floor(percentComplete * 100);
    const roundedSeconds = Math.round(listenedSeconds);
    const durationString = secondsToHHMMSS(roundedSeconds);
    const listenedPercentageDecimal = parseFloat(nextPercent.toFixed(2));

    updateTrackProgressState(trackId, nextPercent);

    const tokenAPI = getCookie("token");
    
    try {
      await axios.post(
        `${API_URL}tracks/update-listened-duration/`,
        {
          track_id: trackId,
          listened_duration: durationString,
          listened_percentage: listenedPercentageDecimal,
        },
        {
          headers: {
            Authorization: `Bearer ${tokenAPI}`,
            "x-api-key": X_API_KEY,
            "Content-Type": "application/json"
          }
        }
      );
      if (nextPercent >= 33 && currentProgress < 33) {
        pendingRefreshRef.current = true;
      }
    } catch (error) {
      console.error(`[ListenTracks] Failed to save progress for track ${trackId}:`, error);
    }
  };

  const syncProgressFromProps = () => {
    if (!activeTracks || activeTracks.length === 0) {
      return;
    }

    setProgressByTrack(prev => {
      const updatedProgress = { ...prev };
      let hasUpdates = false;

      activeTracks.forEach((track) => {
        const listenId = track.listenId;
        if (track.listened_duration && listenId) {
          const currentPercent = prev[listenId] ?? 0;

          if (currentPercent >= 100) {
            return;
          }
          
          const listenedSeconds = track.listened_duration;
          const duration = trackDurationsRef.current[listenId];
          
          if (duration && duration > 0) {
            const percent = Math.min(100, Math.round((listenedSeconds / duration) * 100));

            if (percent > currentPercent || currentPercent === 0) {
              updatedProgress[listenId] = percent;
              hasUpdates = true;
            }

            if (track.id != null) {
              hydrateProgressFromBackend(track.id, listenedSeconds, duration);
            }
          }
        }
      });

      return hasUpdates ? updatedProgress : prev;
    });
  };


  useEffect(() => {

    const hasDurations = Object.keys(trackDurationsRef.current).length > 0;
    if (hasDurations) {
      syncProgressFromProps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTracks]);

  const handleLoadedMetadata = (track, event) => {
    const listenId = track.listenId;
    const audioElement = event?.target;
    if (audioElement?.duration && !isNaN(audioElement.duration)) {
      trackDurationsRef.current[listenId] = audioElement.duration;
      setLoadingTracks(prev => ({
        ...prev,
        [listenId]: false
      }));
      setFailedTracks(prev => {
        const updated = { ...prev };
        delete updated[listenId];
        return updated;
      });
      syncProgressFromProps();
    }
  };

  const handleLoadStart = (track) => {
    const listenId = track.listenId;
    setLoadingTracks(prev => ({
      ...prev,
      [listenId]: true
    }));
    setFailedTracks(prev => {
      const updated = { ...prev };
      delete updated[listenId];
      return updated;
    });
  };

  const handleCanPlay = (track) => {
    setLoadingTracks(prev => ({
      ...prev,
      [track.listenId]: false
    }));
  };

  const retryLoadTrack = (track) => {
    const listenId = track.listenId;
    const audioElement = audioRefs.current[listenId];
    if (audioElement) {
      setFailedTracks(prev => {
        const updated = { ...prev };
        delete updated[listenId];
        return updated;
      });
      setLoadingTracks(prev => ({
        ...prev,
        [listenId]: true
      }));

      audioElement.load();
    }
  };

  const handleProgressUpdate = (track, event) => {
    if (track.id == null) {
      return;
    }

    const listenId = track.listenId;
    const audioElement = event?.target;
    if (!audioElement || !audioElement.duration || Number.isNaN(audioElement.duration)) {
      return;
    }

    const currentTime = audioElement.currentTime;
    const previousTime =
      lastTimeRef.current[listenId] !== undefined
        ? lastTimeRef.current[listenId]
        : currentTime;

    const rawDelta = currentTime - previousTime;
    const isSeek = Math.abs(rawDelta) > 1.5 || rawDelta < 0;
    if (isSeek || audioElement.paused) {
      lastTimeRef.current[listenId] = currentTime;
      return;
    }

    if (rawDelta <= 0) {
      lastTimeRef.current[listenId] = currentTime;
      return;
    }

    const { previousTotal, nextTotal } = recordUniqueListening(
      track.id,
      previousTime,
      currentTime,
      audioElement.duration
    );
    lastTimeRef.current[listenId] = currentTime;

    const chunkThreshold = Math.min(
      MIN_UNIQUE_SECONDS_PER_CHUNK,
      Math.max(1, audioElement.duration * 0.1)
    );

    if (nextTotal < chunkThreshold && previousTotal < chunkThreshold) {
      return;
    }

    const percentComplete = Math.min(
      1,
      (listenedSecondsRef.current[track.id] || 0) / audioElement.duration
    );
    const cacheKey = `${track.id}`;
    const lastSent = progressCacheRef.current[cacheKey] || 0;

    if (percentComplete < lastSent + PROGRESS_INCREMENT && percentComplete !== 1) {
      return;
    }

    progressCacheRef.current[cacheKey] = percentComplete;
    reportListeningProgress({
      trackId: track.id,
      percentComplete,
      listenedSeconds: listenedSecondsRef.current[track.id]
    });
  };

  const handleEnded = (track, event) => {
    if (track.id == null) {
      return;
    }

    const listenId = track.listenId;
    const duration = event?.target?.duration || event?.target?.currentTime || 0;
    const previousTime =
      lastTimeRef.current[listenId] !== undefined
        ? lastTimeRef.current[listenId]
        : 0;

    recordUniqueListening(track.id, previousTime, duration, duration);

    const actualListened = Math.min(
      listenedSecondsRef.current[track.id] || 0,
      duration
    );

    const completionRatio =
      duration > 0 ? actualListened / duration : 0;

    const hasMetCompletion = completionRatio >= MIN_COMPLETION_RATIO;
    const percentToReport = hasMetCompletion ? 1 : completionRatio;
    const secondsToReport = hasMetCompletion ? duration : actualListened;

    progressCacheRef.current[track.id] = percentToReport;
    listenedSecondsRef.current[track.id] = secondsToReport;
    lastTimeRef.current[listenId] = secondsToReport;

    reportListeningProgress({
      trackId: track.id,
      percentComplete: percentToReport,
      listenedSeconds: secondsToReport
    });
  };

  const overallProgressPercent = useMemo(() => {
    if (!activeTracks || activeTracks.length === 0) {
      return 0;
    }
    const total = activeTracks.reduce((acc, track) => {
      return acc + (progressByTrack[track.listenId] ?? 0);
    }, 0);
    return Math.round(total / activeTracks.length);
  }, [activeTracks, progressByTrack]);

  const renderProgressBar = (value) => (
    <div style={{ width: "100%", marginTop: "8px" }}>
      <div
        style={{
          width: "100%",
          height: "8px",
          borderRadius: "999px",
          backgroundColor: "#e9ecef",
          overflow: "hidden",
          position: "relative"
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: "100%",
            background: "var(--branding-theme-color, var(--theme-color, #2fbaad))",
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
        {value}%
      </div>
    </div>
  );

  return (
    <>
      <div
        onClick={() => {
          if(preScanEnabled && preScanDeclarationRequired && !preScanDeclarationSubmitted) {
            return;
          }
          else {
            toggleModal();
          }
        }}
        id={`listen-tracks-tooltip-id-${id}`}
        className={compact ? "release-card-listen-btn" : undefined}
        style={{
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: compact ? "32px" : "30px",
          height: compact ? "32px" : "30px",
          borderRadius: "50%",
          backgroundColor: atleast_33_pct_listened_per_track ? "var(--branding-theme-color, var(--theme-color, #2fbaad))" : "#ff0b4a",
          color: "#fff",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={compact ? undefined : (e) => {
          e.currentTarget.style.opacity = "0.85";
          e.currentTarget.style.transform = "scale(1.05)";
        }}
        onMouseLeave={compact ? undefined : (e) => {
          e.currentTarget.style.opacity = "1";
          e.currentTarget.style.transform = "scale(1)";
        }}
        title="Listen to tracks"
      >
        <i 
          className="fas fa-play" 
          style={{ 
            fontSize: compact ? "10px" : "14px",
            marginLeft: "2px",
            pointerEvents: "none"
          }}
        />
      </div>
      {
        preScanEnabled && !pre_scan_done && (
          <UncontrolledTooltip
            placement="top"
            trigger="hover"
            target={`listen-tracks-tooltip-id-${id}`}
          >
            Please perform pre scan first in order to listen to tracks.
          </UncontrolledTooltip>
        )
      }
      {
        preScanEnabled && preScanDeclarationRequired && !preScanDeclarationSubmitted && (
          <UncontrolledTooltip
            placement="top"
            trigger="hover"
            target={`listen-tracks-tooltip-id-${id}`}
          >
            Please submit the pre-scan declaration first in order to listen to tracks.
          </UncontrolledTooltip>
        )
      }
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <i className="fas fa-music" style={{ color: "var(--branding-theme-color, var(--theme-color, #2fbaad))" }}></i>
            <span>Listen to Tracks</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "0" }}>
          {/* <div style={{ padding: "20px", borderBottom: "1px solid #f0f0f0", backgroundColor: "#fafafa" }}>
            <p style={{ margin: 0, fontSize: "13px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Overall Progress
            </p>
            {renderProgressBar(overallProgressPercent)}
          </div> */}
          <div ref={tracksListRef} style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {loadingFetchedTracks && activeTracks.length === 0 && (
              <div style={{ padding: "20px", color: "#666", fontSize: "13px" }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }} />
                Loading tracks...
              </div>
            )}
            {!loadingFetchedTracks && activeTracks.length === 0 && (
              <div style={{ padding: "20px", color: "#999", fontSize: "13px" }}>
                No tracks available for listening.
              </div>
            )}
            {activeTracks.map((track, index) => {
              const {
                listenId,
                rawSource,
                audioSource,
                trackType,
                pendingDemoBlob,
              } = resolveTrackAudio(track);
              const trackProgress = progressByTrack[listenId] ?? 0;
              const hasPlayableSource = Boolean(audioSource);
              return (
                <div 
                  key={listenId} 
                  style={{ 
                    padding: "20px",
                    borderBottom: index < activeTracks.length - 1 ? "1px solid #f0f0f0" : "none",
                    transition: "background-color 0.2s ease",
                    backgroundColor: "#fff"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "15px", marginBottom: "12px" }}>
                    <div 
                      style={{
                        minWidth: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: "var(--branding-theme-color, var(--theme-color, #2fbaad))",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "600",
                        flexShrink: 0
                      }}
                    >
                      {track.order ? track.order + 1 : index + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 
                        className="fontWeight700" 
                        style={{ 
                          margin: 0,
                          marginBottom: "4px",
                          fontSize: "15px",
                          color: "#333",
                          lineHeight: "1.4"
                        }}
                      >
                        {track.name}
                      </h6>
                      {track.mix_name && (
                        <p style={{ 
                          margin: 0, 
                          fontSize: "13px", 
                          color: "#666",
                          fontStyle: "italic"
                        }}>
                          {track.mix_name}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* {renderProgressBar(trackProgress)} */}
                  {hasPlayableSource || rawSource ? (
                    <div style={{ 
                      marginLeft: "47px",
                      padding: "12px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "6px",
                      border: "1px solid #e9ecef"
                    }}>
                      {pendingDemoBlob ? (
                        <div style={{
                          fontSize: "12px",
                          color: "#666",
                          padding: "8px 0",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}>
                          <i className="fas fa-spinner fa-spin" />
                          Resolving demo upload…
                        </div>
                      ) : failedTracks[listenId] ? (
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "10px",
                          padding: "8px 0"
                        }}>
                          <p style={{ 
                            color: "#dc3545", 
                            fontStyle: "italic", 
                            margin: 0,
                            fontSize: "13px",
                            flex: 1
                          }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: "6px" }}></i>
                            Failed to load audio
                          </p>
                          <button
                            onClick={() => retryLoadTrack(track)}
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              backgroundColor: "var(--branding-theme-color, var(--theme-color, #2fbaad))",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "opacity 0.2s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = "0.85";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = "1";
                            }}
                          >
                            <i className="fas fa-redo" style={{ marginRight: "4px" }}></i>
                            Retry
                          </button>
                        </div>
                      ) : (
                        <>
                          {loadingTracks[listenId] && (
                            <div style={{ 
                              fontSize: "12px", 
                              color: "#666", 
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px"
                            }}>
                              <i className="fas fa-spinner fa-spin"></i>
                              Loading audio...
                            </div>
                          )}
                          <audio
                            ref={(el) => registerListenTrackAudio(listenId, el)}
                            controls
                            id={`track_play_${listenId}`}
                            controlsList="nodownload"
                            preload="metadata"
                            onTimeUpdate={(event) => handleProgressUpdate(track, event)}
                            onEnded={(event) => handleEnded(track, event)}
                            onError={(event) => handleAudioError(track, event)}
                            onLoadedMetadata={(event) => handleLoadedMetadata(track, event)}
                            onLoadStart={() => handleLoadStart(track)}
                            onCanPlay={() => handleCanPlay(track)}
                            style={{ 
                              width: "100%", 
                              outline: "none",
                              height: "32px"
                            }}
                          >
                            <source src={audioSource} type={trackType} />
                            Your browser does not support the audio element.
                          </audio>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ marginLeft: "47px" }}>
                      <p style={{ 
                        color: "#999", 
                        fontStyle: "italic", 
                        margin: 0,
                        fontSize: "13px",
                        padding: "8px 12px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px"
                      }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: "6px" }}></i>
                        Audio source not available
                      </p>
                    </div>
                  )}
              </div>
              );
            })}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ListenTracks;